import { Injectable } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import {
  AlertController,
  ModalController,
  ToastController,
} from "@ionic/angular";
import { format, parseISO } from "date-fns";
import { Observable, Subscription, debounceTime, lastValueFrom } from "rxjs";
import { newGuid } from "src/app/utils/guid";
import { ROUTES } from "src/app/utils/routes";
import { decimalValidator } from "src/app/utils/validators";
import { DeleteActionService } from "../delete-filter-actions.service";
import { StorageService } from "../storage.service";
import {
  AppSettingService,
  InvTranCompleteModel,
  InvTranDetModel,
  InvTranModel,
  InvTranService,
} from "../swagger";
import { FormManager } from "./form-manager";
import { GeolocationService } from "../geolocation.service";

export type InvTranDraftType =
  | "inTran"
  | "rcv"
  | "wo"
  | "returns"
  | "es"
  | "Shelf-Life";

export const invTranDraftKeyPrefix = "invTranDraft:";

/**
 * Every screen built on this form keeps its drafts in its own namespace, so an
 * unfinished Returns entry is never offered when Estimated Sales is opened.
 *
 * These slugs are storage keys, not display strings. `wo` and `rcv` match the
 * keys already written to users' devices and must not change; the rest are
 * spelled out here so renaming a transType later cannot orphan saved drafts.
 */
export const invTranDraftSlugs: Record<InvTranDraftType, string> = {
  wo: "wo",
  rcv: "rcv",
  inTran: "intran",
  returns: "returns",
  es: "es",
  "Shelf-Life": "shelf-life",
};

export const invTranCreateDraftKey = (transType: InvTranDraftType): string =>
  `${invTranDraftKeyPrefix}${invTranDraftSlugs[transType]}:create`;

export const invTranUpdateDraftKeyPrefix = (
  transType: InvTranDraftType,
): string => `${invTranDraftKeyPrefix}${invTranDraftSlugs[transType]}:update:`;

/**
 * Drop every inventory-transaction draft on the device — used when the user
 * abandons the page and when the session ends, so a shared tablet never offers
 * one user's unsaved work to the next.
 */
export const clearAllInvTranDrafts = async (
  storage: StorageService,
): Promise<void> => {
  try {
    const keys = await storage.keys();
    const keysToRemove = (keys ?? []).filter((key) =>
      key.startsWith(invTranDraftKeyPrefix),
    );
    await Promise.all(keysToRemove.map((key) => storage.remove(key)));
  } catch (e) {
    // Ignore storage errors (private mode, quota, etc.).
  }
};

@Injectable()
export class CreateInventoryTransactionForm {
  form: FormGroup;
  resource: InvTranModel;
  formattedDate: string = "";
  dateValue = format(new Date(), "yyyy-MM-dd");
  someThingChange: boolean = false;
  fulfillDetailItems: any[] = [];
  selectedFulfillmentState: number | null = null;
  fulfillmentSearchTerm: string = "";
  transType: InvTranDraftType;
  private formChangesSub?: Subscription;

  get lines(): FormArray {
    return this.form.get("detail") as FormArray;
  }

  constructor(
    private router: Router,
    private modalController: ModalController,
    private formManager: FormManager,
    private alertCtrl: AlertController,
    private invTranService: InvTranService,
    private appSettingService: AppSettingService,
    private deleteAction: DeleteActionService,
    private toastCtrl: ToastController,
    private storageService: StorageService,
    private geolocationService: GeolocationService,
    private fb: FormBuilder,
  ) {}

  async initializeForm(
    inv?: InvTranModel,
    transType?: InvTranDraftType,
  ) {
    this.transType = transType;
    this.resource = inv;
    let data: any = { ...inv };
    // A new transaction needs an identity before the server assigns one, so an
    // insert that gets re-sent is recognised as the same transaction rather
    // than creating a second. One the server already knows keeps whatever it
    // holds — inventing a value here would send one it has never seen.
    const invTranGuid = data.guid || (data.invTranId ? undefined : newGuid());
    this.form = this.fb.group({
      linkInvTranId: [data.linkInvTranId ? data.linkInvTranId : 0],
      invTranId: [data.invTranId, []],
      guid: [invTranGuid],
      invTranNumber: [data.invTranNumber, []],
      invTranDocTypeId: [data.invTranDocTypeId, [Validators.required]],
      invTranTypeId: [data.invTranTypeId, [Validators.required]],
      locationId: [data.locationId, [Validators.required]],
      siteId: [data.siteId, [Validators.required]],
      invTranDate: [data.invTranDate, [Validators.required]],
      reference: [data.reference, []],
      statusId: [data.statusId ? data.statusId : 1, [Validators.required]],
      workOrderNumber: [""],
      detail: this.fb.array([]),
      customerNumber: [
        data && (data as any).customerNumber
          ? (data as any).customerNumber
          : null,
        [],
      ],
      customerName: [
        data && (data as any).customerName ? (data as any).customerName : null,
      ],
      customerOrderId: [data.customerOrderId, []],
    });

    if (transType === "wo" || transType === "returns") {
      this.form.get("customerNumber").setValidators([Validators.required]);
      this.form.get("customerNumber").updateValueAndValidity();
    }

    if (data && data.invTranDate) {
      this.dateChanged(data.invTranDate);
    } else {
      this.formattedDate = "";
      this.dateValue = format(new Date(), "yyyy-MM-dd");
      this.dateChanged(this.dateValue);
    }

    if (data && data.invTranDate) {
      if (data.detail && data.detail.length) {
        data.detail.map(async (item) => {
          await this.addLinesForm(item, undefined, false);
        });
      }
    }
    this.setupWoDraftAutosave();
    // Reset dirty state after initialization so drafts only save on user changes.
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  dateChanged(value) {
    this.dateValue = value;
    this.form.controls.invTranDate.setValue(value);
    this.form.controls.invTranDate.updateValueAndValidity(value);
    this.formattedDate = format(parseISO(value), "MMM dd yyyy");
  }

  async addLinesForm(
    item?: InvTranDetModel,
    selectedFullFillInvTranDet?: any,
    saveDraft?: boolean,
  ) {
    let formGroup = await this.buildLinesForm(item, selectedFullFillInvTranDet);
    await this.addInArrayItem(formGroup, item, false, false, saveDraft);
  }

  async addInArrayItem(
    formGroup: FormGroup,
    item?: InvTranDetModel,
    changeOrder: boolean = false,
    replaceExistingItem: boolean = false,
    saveDraft: boolean = true,
  ) {
    formGroup = this.detachFromSource(formGroup);

    // ✅ Replace mode: do NOT push/insert new. Only update an existing one.
    if (replaceExistingItem) {
      const idx = this.findExistingLineIndex(formGroup, item);

      // If found, replace the control at same index
      if (idx > -1) {
        // The replacement is the same line re-scanned, so it keeps that line's
        // identity; a fresh guid would let a retried save insert it twice.
        this.setLineGuid(formGroup, this.lines.at(idx).get("guid")?.value);
        this.lines.setControl(idx, formGroup);
        this.someThingChange = true;
      }

      // If not found: do nothing (no new push/insert) — matches your requirement
      return;
    }

    this.ensureUniqueLineGuid(formGroup);

    if (item && item.invTranDetId) {
      this.lines.push(formGroup);
      setTimeout(() => {
        // Keep a draft snapshot for WO transactions so users can restore after a crash.
        if (saveDraft) {
          void this.saveDraftFromForm();
        }
      }, 500);
    } else {
      if (this.fulfillDetailItems && this.fulfillDetailItems.length) {
        const isSameItemLikeFullFill = await this.isNotSameItemLikeFullFill(
          formGroup,
        );
        if (isSameItemLikeFullFill) {
          await this.openToast(
            "Item not in the Fulfill Customer Order and can not be added",
            "danger",
          );
          return;
        }
      }
      if (changeOrder) {
        this.lines.push(formGroup);
        setTimeout(() => {
          // Keep a draft snapshot for WO transactions so users can restore after a crash.
          if (saveDraft) {
            void this.saveDraftFromForm();
          }
        }, 500);
      } else {
        this.lines.insert(0, formGroup);
        if (saveDraft) {
          void this.saveDraftFromForm();
        }
      }
      this.someThingChange = true;
    }
  }

  /**
   * Modals hand us their own working FormGroup, which can still carry the valueChanges
   * subscriptions they set up while open. Store a detached copy instead, so a modal that has
   * been closed can never write into a saved line (it used to rewrite quantity as
   * units * the case factor captured when the item was scanned).
   */
  private detachFromSource(formGroup: FormGroup): FormGroup {
    const controlNames = Object.keys(formGroup.controls);
    const isFlatGroup =
      controlNames.length &&
      controlNames.every(
        (name) => formGroup.controls[name] instanceof FormControl,
      );
    if (!isFlatGroup) return formGroup;

    const detached = this.fb.group({});
    for (const name of controlNames) {
      const source = formGroup.controls[name] as FormControl;
      detached.addControl(
        name,
        new FormControl(
          { value: source.value, disabled: source.disabled },
          {
            validators: source.validator,
            asyncValidators: source.asyncValidator,
          },
        ),
      );
    }
    return detached;
  }

  /**
   * The item modals build each new line by copying controls from the same
   * source group, so adding several without closing one hands every line that
   * group's guid — and the server refuses the save because two lines claim a
   * single identity. Groups built without a guid control get one here too.
   * Only lines it has not stored yet get assigned: rewriting a stored line's
   * guid would let a retried save insert it again, which is the very thing the
   * guid exists to prevent.
   */
  private ensureUniqueLineGuid(formGroup: FormGroup): void {
    if (formGroup.get("invTranDetId")?.value) return;
    const guid = formGroup.get("guid")?.value;
    const taken =
      !guid ||
      this.lines.controls.some(
        (line) => line !== formGroup && line.get("guid")?.value === guid,
      );
    if (taken) {
      this.setLineGuid(formGroup, newGuid());
    }
  }

  private setLineGuid(formGroup: FormGroup, guid: string | undefined): void {
    const control = formGroup.get("guid");
    if (control) {
      control.setValue(guid);
    } else {
      formGroup.addControl("guid", new FormControl(guid));
    }
  }

  /**
   * Same reasoning as the transaction guid: a line the server has never stored
   * needs its own identity so a re-sent save updates that row instead of
   * inserting a duplicate. Lines that already carry an invTranDetId keep what
   * the server holds.
   */
  private lineGuidFor(item?: InvTranDetModel): string | undefined {
    return (item && item.guid) || (item && item.invTranDetId ? undefined : newGuid());
  }

  // Helper: find existing line by invTranDetId first, else itemNumber
  private findExistingLineIndex(
    formGroup: FormGroup,
    item?: InvTranDetModel,
  ): number {
    const targetInvTranDetId =
      item?.invTranDetId ?? formGroup.get("invTranDetId")?.value;

    if (targetInvTranDetId) {
      const byId = this.lines.controls.findIndex(
        (c) => c.get("invTranDetId")?.value === targetInvTranDetId,
      );
      if (byId > -1) return byId;
    }

    const targetItemNumber = formGroup.get("itemNumber")?.value;
    if (targetItemNumber) {
      return this.lines.controls.findIndex(
        (c) => c.get("itemNumber")?.value === targetItemNumber,
      );
    }

    return -1;
  }

  async isNotSameItemLikeFullFill(formGroup: FormGroup): Promise<Boolean> {
    try {
      let res = await lastValueFrom(
        this.appSettingService.apiAppsettingByGroupAndNameGet({
          appSettingGroup: "APP",
          appSettingName: "OnlyWorkOrderFulfillWithCOItems",
        }),
      );
      if (res && res.appSettingValue === "Yes") {
        const found = this.fulfillDetailItems.find(
          (it) =>
            formGroup &&
            formGroup.value &&
            it.itemNumber === formGroup.value.itemNumber,
        );
        return found ? false : true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }

  private parseImportedSellByDate(
    raw: string | null | undefined,
  ): string | null {
    if (!raw) return null;
    // .NET default DateTime ("0001-01-01T...") means "no date" — treat as null
    if (raw.startsWith("0001-01-01")) return null;
    return raw;
  }

  buildLinesForm(item?: InvTranDetModel, selectedFullFillInvTranDet?: any) {
    return this.fb.group({
      guid: [this.lineGuidFor(item)],
      invTranDetId: [item && item.invTranDetId ? item.invTranDetId : 0, []],
      invTranId: [item && item.invTranId ? item.invTranId : 0, []],
      itemNumber: [item && item.itemNumber, [Validators.required]],
      itemDescription: [item && item.itemDescription, []],
      quantity: [
        item && item.quantity,
        [Validators.required, decimalValidator({ required: true, min: 0 })],
      ],
      uOfM: [item && item.uOfM, [Validators.required]],
      units: [item && item.units ? item.units : !item ? 1 : 0, []],
      workOrderId: [item && item.workOrderId ? item.workOrderId : 0, []],
      statusId: [
        item && item.statusId ? item.statusId : 1,
        [Validators.required],
      ],
      uniqueIdentifierType: [
        item && item.uniqueIdentifierType ? item.uniqueIdentifierType : null,
      ],
      uniqueIdentifierTypeDisplay: [
        item && item.uniqueIdentifierTypeDisplay
          ? item.uniqueIdentifierTypeDisplay
          : "",
      ],
      uniqueIdentifier: [
        item && item.uniqueIdentifier ? item.uniqueIdentifier : "",
      ],
      batchIdentifierType: [
        item && item.batchIdentifierType ? item.batchIdentifierType : null,
      ],
      batchIdentifierTypeDisplay: [
        item && item.batchIdentifierTypeDisplay
          ? item.batchIdentifierTypeDisplay
          : "",
      ],
      batchIdentifier: [
        item && item.batchIdentifier ? item.batchIdentifier : "",
      ],
      sellByDate: [this.parseImportedSellByDate(item?.sellByDate)],
      barcodeSource: [item && item.barcodeSource ? item.barcodeSource : ""],
      reference: [item && (item as any).reference, []],
      linkCustomerOrderDetId: [
        item && item.linkCustomerOrderDetId
          ? item.linkCustomerOrderDetId
          : selectedFullFillInvTranDet &&
            selectedFullFillInvTranDet.customerOrderDetId
          ? selectedFullFillInvTranDet.customerOrderDetId
          : null,
      ],
      customerOrderNumber: [item && (item as any).customerOrderNumber, []],
      invTranNumber: [item && (item as any).invTranNumber, []],
    });
  }

  buildReceivingLinesForm(
    item?: InvTranDetModel,
    selectedFullFillInvTranDet?: any,
  ) {
    return this.fb.group({
      guid: [this.lineGuidFor(item)],
      invTranDetId: [item && item.invTranDetId ? item.invTranDetId : 0, []],
      invTranId: [item && item.invTranId ? item.invTranId : 0, []],
      itemNumber: [item && item.itemNumber, [Validators.required]],
      itemDescription: [item && item.itemDescription, []],
      quantity: [
        item && item.quantity,
        [Validators.required, decimalValidator({ required: true, min: 0 })],
      ],
      uOfM: [item && item.uOfM, [Validators.required]],
      units: [item && item.units ? item.units : 1, []],
      statusId: [
        item && item.statusId ? item.statusId : 1,
        [Validators.required],
      ],
      uniqueIdentifierType: [3],
      uniqueIdentifierTypeDisplay: [
        item && item.uniqueIdentifierTypeDisplay
          ? item.uniqueIdentifierTypeDisplay
          : "",
      ],
      uniqueIdentifier: [
        item && item.uniqueIdentifier ? item.uniqueIdentifier : "",
      ],
      batchIdentifierType: [1],
      batchIdentifierTypeDisplay: [
        item && item.batchIdentifierTypeDisplay
          ? item.batchIdentifierTypeDisplay
          : "",
      ],
      batchIdentifier: [
        item && item.batchIdentifier ? item.batchIdentifier : "",
      ],
      sellByDate: [this.parseImportedSellByDate(item?.sellByDate)],
      reference: [item && (item as any).reference, []],
      linkCustomerOrderDetId: [
        item && item.linkCustomerOrderDetId
          ? item.linkCustomerOrderDetId
          : selectedFullFillInvTranDet &&
            selectedFullFillInvTranDet.customerOrderDetId
          ? selectedFullFillInvTranDet.customerOrderDetId
          : null,
      ],
    });
  }

  async deleteLine(
    index: number,
    line: FormGroup,
    closeModal: boolean = false,
  ) {
    if (line.value.invTranDetId) {
      let data = line as any;
      let message = "Are you sure you want to delete this?";
      let deleteFn = async () => {
        line.controls.statusId.setValue(3);
        this.form.updateValueAndValidity();
        this.form.markAllAsTouched();
        this.form.markAsDirty();
      };
      await this.deleteAction.onDelete(data, deleteFn, message);
    } else if (line.value.isCaptureFromEstimatesSalesList) {
      line.controls.statusId.setValue(3);
      this.form.updateValueAndValidity();
      this.form.markAllAsTouched();
      this.form.markAsDirty();
    } else {
      this.lines.removeAt(index);
    }
    this.someThingChange = true;
    // Update the draft after line removal to avoid restoring stale data.
    void this.saveDraftFromForm();
    if (closeModal) {
      await this.modalController.dismiss();
    }
  }

  async openToast(message: string, color: string = "dark") {
    if (message) {
      let toast = await this.toastCtrl.create({
        message: message,
        buttons: [{ text: "Done", role: "cancel" }],
        duration: 3000,
        color: color,
      });
      await toast.present();
    }
  }

  async createVoid(
    transType: InvTranDraftType,
  ) {
    let message = "Are you sure you want to void this?";
    let headerMessage = "Void?";
    let deleteFn = async () => {
      let id = this.resource.invTranId;
      await lastValueFrom(
        this.invTranService.apiInvTranDeleteIdDelete({
          id: id,
        }),
      );
      this.clearFormChangeSubscription();
      await this.clearDraftForCurrentForm();
      this.form = undefined;
      await this.backToOperationsPage(transType);
    };
    await this.deleteAction.onDelete(
      this.resource,
      deleteFn,
      message,
      true,
      headerMessage,
      "Voided Successfully",
      "Yes, Void",
    );
  }

  async deleteInvTranWorkOrder(
    data: InvTranModel,
    transType: InvTranDraftType,
  ) {
    if (data && data.customerOrderNumber) {
      let message = "Are you sure you want to void this Work Order?";
      let headerMessage = "Void Work Order?";
      let deleteFn = async () => {
        let id = this.resource.invTranId;
        await lastValueFrom(
          this.invTranService.apiInvTranDeleteInvTranWorkOrderIdDelete({
            id: id,
          }),
        );
        this.clearFormChangeSubscription();
        await this.clearDraftForCurrentForm();
        this.form = undefined;
        await this.backToOperationsPage(transType);
      };
      await this.deleteAction.onDelete(
        this.resource,
        deleteFn,
        message,
        true,
        headerMessage,
        "Successfully Voided Inv Tran Work Order",
        "Yes, Void",
      );
    }
  }

  async createSubmit(
    transType: InvTranDraftType,
  ) {
    const type = `${
      transType === "rcv"
        ? "Receiving"
        : transType === "wo"
        ? "Work Order"
        : transType === "es"
        ? "Estimated Sales"
        : transType === "Shelf-Life"
        ? "Shelf Life"
        : transType === "returns"
        ? "Return"
        : ""
    }`;
    let alert = await this.alertCtrl.create({
      header: `Submit a ${type}?`,
      message: `Are you sure you want to submit this ${type}?`,
      buttons: [
        { text: "Cancel", role: "cancel", cssClass: "cancel-btn" },
        {
          text: "Yes, Submit",
          role: "confirm",
          cssClass: "confirm-btn",
        },
      ],
      mode: "md",
      cssClass: "delete-alert",
    });
    await alert.present();
    let { role } = await alert.onDidDismiss();
    if (role === "confirm") {
      await this.create(transType);
    }
  }

  async create(
    transType: InvTranDraftType,
  ) {
    let data = this.form?.value;
    data.detail = this.lines.value.map((val) => {
      return { ...val, workOrderId: val.workOrderId ? val.workOrderId : 0 };
    });
    this.dropEmptyGuids(data);
    if ((transType === "wo" || transType === "returns") && this.resource) {
      data.fulfillmentDetail = this.resource.customerOrderDetail;
    }
    let submitFn = async () => {
      let result: Observable<any>;
      let res: any;
      if (data.invTranId) {
        result = this.invTranService.apiInvTranUpdatePut({
          invTranUpdateModel: data,
        });
        res = await lastValueFrom(result);
      } else {
        data.detail = data.detail.filter((tran) => tran.statusId !== 3);
        const locationStr =
          await this.geolocationService.getCurrentGeoLocation();
        if (locationStr) {
          const [longitude, latitude] = locationStr.split(",");
          data.geoLocation = { latitude, longitude };
        }
        result = this.invTranService.apiInvTranInsertPost({
          invTranInsertModel: data,
        });
        res = await lastValueFrom(result);
      }
      this.clearFormChangeSubscription();
      await this.clearDraftForCurrentForm();
      this.form = undefined;
      await this.backToOperationsPage(transType);
    };
    let toast: string;
    if (data.invTranId) {
      toast = "Updated successfully";
    } else {
      toast = "Added successfully";
    }
    await this.formManager.submit(this.form!, submitFn, toast);
  }

  /**
   * Records created before guids existed have none, and an absent form control
   * reads as null. Send the key only when there is a value behind it, so the
   * server falls back to the record's id rather than matching on an empty key.
   */
  private dropEmptyGuids(data: any): void {
    if (data && !data.guid) {
      delete data.guid;
    }
    for (const line of (data && data.detail) || []) {
      if (line && !line.guid) {
        delete line.guid;
      }
    }
  }

  backToOperationsPage(
    transType: InvTranDraftType,
  ) {
    if (transType === "inTran") {
      this.router.navigate([ROUTES.OPERATIONS_INVENTORY_TRANSACTIONS_PAGE], {
        queryParams: {
          reload: "true",
        },
        queryParamsHandling: "merge", // remove to replace all query params by provided
      });
    }
    if (transType === "rcv") {
      this.router.navigate([ROUTES.OPERATIONS_RECEIVING_PAGE], {
        queryParams: {
          reload: "true",
        },
        queryParamsHandling: "merge", // remove to replace all query params by provided
      });
    }
    if (transType === "wo") {
      this.router.navigate([ROUTES.OPERATIONS_WORK_ORDER_PAGE], {
        queryParams: {
          reload: "true",
        },
        queryParamsHandling: "merge", // remove to replace all query params by provided
      });
    }
    if (transType === "es") {
      this.router.navigate([ROUTES.OPERATIONS_ESTIMATED_SALES_PAGE], {
        queryParams: {
          reload: "true",
        },
        queryParamsHandling: "merge", // remove to replace all query params by provided
      });
    }
    if (transType === "Shelf-Life") {
      this.router.navigate([ROUTES.OPERATIONS_SHELF_LIFE_PAGE], {
        queryParams: {
          reload: "true",
        },
        queryParamsHandling: "merge", // remove to replace all query params by provided
      });
    }
    if (transType === "returns") {
      this.router.navigate([ROUTES.OPERATIONS_RETURNS_PAGE], {
        queryParams: {
          reload: "true",
        },
        queryParamsHandling: "merge", // remove to replace all query params by provided
      });
    }
  }

  isInvTranAlreadyAdded(data: any): boolean {
    const invTrans = this.lines.value;
    if (invTrans && invTrans.length && data && data.validateItemIdentifier) {
      if (!data.uniqueIdentifier) {
        data.uniqueIdentifier = "";
      }
      if (!data.batchIdentifier) {
        data.batchIdentifier = "";
      }
      const found = invTrans
        .filter((tran) => tran.statusId !== 3)
        .find((trans) => {
          return (
            trans.itemNumber == data.itemNumber &&
            trans.uniqueIdentifier == data.uniqueIdentifier &&
            trans.batchIdentifier == data.batchIdentifier
          );
        });
      return found ? true : false;
    }
    return false;
  }

  isReceivingAlreadyAdded(data: any): boolean {
    const invTrans = this.lines.value;
    if (invTrans && invTrans.length && data && data.validateItemIdentifier) {
      if (!data.uniqueIdentifier) {
        data.uniqueIdentifier = "";
      }
      if (!data.batchIdentifier) {
        data.batchIdentifier = "";
      }
      const found = invTrans
        .filter((tran) => tran.statusId !== 3)
        .find((trans) => {
          return (
            trans.uniqueIdentifier == data.uniqueIdentifier &&
            trans.batchIdentifier == data.batchIdentifier
          );
        });
      return found ? true : false;
    }
    return false;
  }

  async completeWorkOrder(data: InvTranCompleteModel) {
    if (data && data.invTranId) {
      let alert = await this.alertCtrl.create({
        header: `Complete Work Order?`,
        message: `Are you sure you want to complete this Work Order?`,
        buttons: [
          { text: "Cancel", role: "cancel", cssClass: "cancel-btn" },
          {
            text: "Yes, Complete",
            role: "confirm",
            cssClass: "confirm-btn",
          },
        ],
        mode: "md",
        cssClass: "delete-alert",
      });
      await alert.present();
      let { role } = await alert.onDidDismiss();
      if (role === "confirm") {
        this.dropEmptyGuids(data);
        let submitFn = async () => {
          data.fulfillmentDetail = this.resource.customerOrderDetail;
          const result = await lastValueFrom(
            this.invTranService.apiInvTranCompleteWorkOrderPut({
              invTranCompleteModel: data,
            }),
          );
          if (result && result.sopDocumentNumber && result.sopDocumentType) {
            this.clearFormChangeSubscription();
            await this.clearDraftForCurrentForm();
            let message = `${result.sopDocumentType} # ${result.sopDocumentNumber}`;
            let alert = await this.alertCtrl.create({
              header: `Work Order Completed`,
              message: message,
              backdropDismiss: false,
              buttons: [
                {
                  text: "Accept",
                  role: "accept",
                  cssClass: "cancel-btn",
                  handler: async () => {
                    this.someThingChange = false;
                    await this.router.navigate(
                      [ROUTES.OPERATIONS_WORK_ORDER_PAGE],
                      {
                        queryParams: {
                          reload: "true",
                        },
                        queryParamsHandling: "merge", // remove to replace all query params by provided
                      },
                    );
                  },
                },
              ],
              mode: "md",
              cssClass: "delete-alert",
            });
            await alert.present();
          }
        };
        await this.formManager.submit(
          this.fb.group({}),
          submitFn,
          "Updated successfully",
        );
      }
    }
  }

  async getCreateDraft(): Promise<WoInvTranDraft | null> {
    const key = this.createDraftKey();
    return key ? await this.readWoDraft(key) : null;
  }

  async getUpdateDraft(invTranId: number): Promise<WoInvTranDraft | null> {
    const key = this.updateDraftKey(invTranId);
    return key ? await this.readWoDraft(key) : null;
  }

  async clearCreateDraft(): Promise<void> {
    const key = this.createDraftKey();
    if (key) await this.clearWoDraft(key);
  }

  async clearUpdateDraft(invTranId: number): Promise<void> {
    const key = this.updateDraftKey(invTranId);
    if (key) await this.clearWoDraft(key);
  }

  async clearCurrentDraft(): Promise<void> {
    await this.clearDraftForCurrentForm();
  }

  markUnsaved(): void {
    this.someThingChange = true;
  }

  markSaved(): void {
    this.someThingChange = false;
    // `dirty` is the other half of the unsaved-change signal — leaving it set
    // keeps the autosave firing and the leave-page guard tripping after a save.
    this.form?.markAsPristine();
  }

  /**
   * Single source of truth for "the user has unsaved work". Controls edited
   * through a value accessor set `dirty`; controls patched programmatically do
   * not, and their handlers call markUnsaved() instead. The autosave and the
   * leave-page guard must read the same combination.
   */
  get hasUnsavedChanges(): boolean {
    return this.someThingChange || !!this.form?.dirty;
  }

  clearFormChangeSubscription(): void {
    if (this.formChangesSub) {
      this.formChangesSub.unsubscribe();
      this.formChangesSub = undefined;
    }
  }

  /** null when this screen does not keep drafts. */
  private get draftSlug(): string | null {
    return invTranDraftSlugs[this.transType] ?? null;
  }

  private createDraftKey(): string | null {
    const slug = this.draftSlug;
    return slug ? `${invTranDraftKeyPrefix}${slug}:create` : null;
  }

  private updateDraftKey(invTranId: number): string | null {
    const slug = this.draftSlug;
    if (!slug || !invTranId) return null;
    return `${invTranDraftKeyPrefix}${slug}:update:${invTranId}`;
  }

  /**
   * The key this form currently maps to: a saved record keys by its id, a
   * brand-new one by the screen's create key.
   */
  private currentDraftKey(): string | null {
    const invTranId = this.form?.getRawValue()?.invTranId;
    return invTranId ? this.updateDraftKey(invTranId) : this.createDraftKey();
  }

  private setupWoDraftAutosave(): void {
    this.clearFormChangeSubscription();
    if (!this.form || !this.draftSlug) return;

    this.formChangesSub = this.form.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        if (this.shouldSaveDraft()) {
          void this.saveDraftFromForm();
        }
      });
  }

  private shouldSaveDraft(): boolean {
    if (!this.form || !this.draftSlug) return false;
    return this.hasUnsavedChanges;
  }

  async saveDraftFromForm(): Promise<void> {
    if (!this.form) return;
    const key = this.currentDraftKey();
    if (!key) return;
    const formValue = this.form.getRawValue();
    const customerNumber = formValue?.customerNumber;
    const customerName = formValue?.customerName;
    let transNumber = "";
    const invTranId = formValue?.invTranId ? formValue.invTranId : null;
    if (this.resource) {
      if (this.resource.workOrderFlowDocuments) {
        transNumber = this.resource.workOrderFlowDocuments;
      } else {
        transNumber = `${this.resource.invTranNumber}`;
      }
    }
    const draft: WoInvTranDraft = {
      meta: {
        invTranId,
        customerNumber,
        customerName,
        workOrderNumber: transNumber,
        savedAt: new Date().toISOString(),
      },
      form: {
        ...formValue,
        customerOrderDetail:
          this.resource && this.resource.customerOrderDetail
            ? this.resource.customerOrderDetail
            : [],
      },
    };

    await this.writeWoDraft(key, draft);
  }

  async clearDraftForCurrentForm(): Promise<void> {
    // Without the form there is no record id, and currentDraftKey() would fall
    // back to the create key — deleting a different draft than the caller meant.
    if (!this.form) return;
    const key = this.currentDraftKey();
    if (key) await this.clearWoDraft(key);
  }

  private async readWoDraft(key: string): Promise<WoInvTranDraft | null> {
    try {
      const raw = await this.storageService.get(key);
      if (!raw) return null;
      return JSON.parse(raw) as WoInvTranDraft;
    } catch (e) {
      return null;
    }
  }

  private async writeWoDraft(
    key: string,
    draft: WoInvTranDraft,
  ): Promise<void> {
    try {
      await this.storageService.set(key, JSON.stringify(draft));
    } catch (e) {
      // Ignore storage errors (quota, private mode, etc.).
    }
  }

  private async clearWoDraft(key: string): Promise<void> {
    try {
      await this.storageService.remove(key);
    } catch (e) {
      // Ignore storage errors (quota, private mode, etc.).
    }
  }
}

export type WoInvTranDraft = {
  meta: {
    invTranId: number | null;
    customerNumber: string | number;
    customerName: string;
    workOrderNumber: string | number;
    savedAt: string;
  };
  form: any;
};
